import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

// Fonts are bundled from npm rather than fetched from Google Fonts at runtime:
// fonts.googleapis.com is unreliable from Russia, and an installed PWA has to
// render correctly with no network at all.
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/space-grotesk/700.css';
import '@fontsource/public-sans/400.css';
import '@fontsource/public-sans/500.css';
import '@fontsource/public-sans/600.css';

import { App } from './App.tsx';
import { AuthProvider } from './auth/AuthContext.tsx';
import { watchForUpdates } from './lib/appUpdate.ts';
import { installClientErrorReporting } from './lib/errorReporter.ts';
import { reportAppExit } from './lib/nativeExit.ts';
import './styles/base.css';

// Before the first render, deliberately: an error thrown on the way up is one
// of the things this is here to catch, and whatever the last session could not
// send is still in localStorage waiting for a network.
installClientErrorReporting();

// Beside it rather than inside it: this asks the shell how the app last died,
// and the answer is filed through the reporter above. Nothing on the phone
// records that anywhere else, and an app that disappeared on its way up leaves
// no other trace at all.
void reportAppExit();

watchForUpdates();

// The browser restores a scroller's position on its own after a reload — which
// in the shell includes the WebView being rebuilt around a renderer that died —
// and it does so asynchronously, after the thread has already been put where it
// belongs. That second landing is a stale offset from the page's previous life,
// so scroll positions here are set only by the code that owns them.
history.scrollRestoration = 'manual';

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <App />
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
);
